import React from 'react';
import PropTypes from 'prop-types';
//import classNames from 'classnames';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
//import FormHelperText from '@mui/material/FormHelperText';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import styled from 'src/ui/library/styled';
import classNames from 'classnames';
import DOMPurify from 'dompurify';
import iconTransform from 'src/helpers/iconTransform.js';
import ErrorIcon from '@mui/icons-material/Error';
import intl from 'react-intl-universal';

const styles = (theme, props) => ({
  root: {},
  label: {
    color: theme.colors.eyConfidentGrey,
    width: '100%', // IE 11 center align fix
    textAlign: props.align === 'C' ? 'center' : 'left', // IE 11 center align fix
  },
  labelFocused: {
    color: `${theme.colors.eyConfidentGrey} !important`,
    width: '100%',
  },
  formControl: {
    display: 'flex',
    width: '100%',
  },
  formControlLabel: {
    position: 'relative',
    left: '-5px',
    fontSize: '1rem',
    '&:hover, &:focus-within': {
      textDecoration: 'underline',
    },
  },
  radioButton: {
    color: theme.colors.eyConfidentGrey,
  },
  radioButtonChecked: {
    color: `${theme.colors.eyConfidentGrey} !important`,
  },
  group: {
    flexDirection: 'row',
    justifyContent: props.align === 'C' ? 'center' : 'left',
  },
  hasError: {
    color: `${theme.colors.red} !important`,
  },
  radioErrorText: {
    color: `${theme.colors.red}`,
    margin: '5px 0 5px 0px',
    fontSize: '12px',
  },
});

class RadioBase extends React.Component {
  static propTypes = {
    /** Pre-defined custom styles to override RadioBase */
    styleType: PropTypes.string,
    /** Classname to be applied to <Radio> */
    className: PropTypes.string,
    /** Value of `input` element */
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /** Text used for input label */
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.element]),
    /** If `true`, the label is displayed as required. */
    required: PropTypes.any,
    /** Callback fired when value is changed */
    onChange: PropTypes.func,

    hasError: PropTypes.bool,

    description: PropTypes.string,
  };

  static defaultProps = {
    styles: {},
    required: false,
    hasError: false,
    className: '',
  };

  state = {
    value: this.props.value === undefined || this.props.value === null ? undefined : String(this.props.value),
    isDisabled: this.props.readOnly,
  };

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.value !== this.props.value) {
      this.setState({
        value: this.props.value === undefined || this.props.value === null ? undefined : String(this.props.value),
      });
    }
  }

  handleChange = event => {
    if (this.props.onChange) {
      this.props.onChange(event);
    }
    //if lockAfterValueChange prop is true , then disable field on change
    const isDisabled = this.props.lockAfterValueChange ? true : this.state.isDisabled;
    this.setState({
      value: event.target.value,
      isDisabled,
    });
  };

  render() {
    const { classes, id, label, required, options, hasError, errorText, description, align, icon } = this.props;
    const rootClass = classNames({
      [classes.hasError]: this.props.hasError,
    });

    return (
      <FormControl component="fieldset" className={classes.formControl}>
        {label && (
          <div className={`control-wrapper ${align === 'C' ? 'justifyCenter' : ''}`}>
            {icon && <div className={'control-icons control-icons-inline'}></div>}
            <div className={'control-icons-inline-controls fullWidth label-field'}>
              <span component="legend" className="control-label">
                <FormLabel
                  component="legend"
                  classes={{
                    root: classes.label,
                    focused: classes.labelFocused,
                    asterisk: this.props.classes.labelAsterisk,
                  }}>
                  {label}
                  {required && intl.get('questionnaire.requiredLabel')}
                </FormLabel>
              </span>
            </div>
          </div>
        )}
        {description && (
          <div className={`control-wrapper ${align === 'C' ? 'justifyCenter' : ''}`}>
            {icon && <div className={'control-icons control-icons-inline'}></div>}
            <div className={'control-icons-inline-controls fullWidth'}>
              <span component="legend" className="control-label">
                <span component="legend" className="control-label">
                  {description}
                </span>
              </span>
            </div>
          </div>
        )}
        <div className={`control-wrapper ${align === 'C' ? 'justifyCenter' : ''}`}>
          {icon && <div className={'control-icons control-icons-inline'}>{iconTransform(icon)}</div>}
          <div className={'control-icons-inline-controls fullWidth'}>
            <RadioGroup
              aria-label={label ? label : 'radio group'}
              id={id}
              name={id}
              className={classes.group}
              value={this.state.value}
              onChange={this.handleChange}>
              {options.map((option, index) => {
                return (
                  <FormControlLabel
                    key={index}
                    value={String(option.value)}
                    classes={{ label: classes.formControlLabel }}
                    disabled={this.state.isDisabled}
                    control={
                      <Radio
                        checked={String(option.value) === this.state.value}
                        classes={{
                          root: classes.radioButton,
                          checked: classes.radioButtonChecked,
                        }}
                        className={rootClass}
                      />
                    }
                    label={option.label}
                  />
                );
              })}
            </RadioGroup>
          </div>
        </div>
        {hasError && errorText && (
          <p className={classes.radioErrorText}>
            {' '}
            <ErrorIcon sx={{ verticalAlign: 'bottom', fontSize: 14 }} />{' '}
            <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(errorText) }} />
          </p>
        )}
      </FormControl>
    );
  }
}

export { RadioBase };
export default styled(RadioBase)(styles);
